/**
 * Migration 0102 — /account/household.
 *
 * One `customers` record, many logins. The primary contact sees every
 * member, invites co-owners by email, toggles each member's permissions and
 * can remove them. A member sees the household read-only and can leave.
 */
import { type CSSProperties, type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { CustomerApiError, customerApi } from '../customer_api';

export interface HouseholdPermissions {
  canBook: boolean;
  canPay: boolean;
  canEditPets: boolean;
  receivesNotifications: boolean;
}

export interface HouseholdMember {
  id: string;
  role: 'primary' | 'member';
  status: 'active' | 'pending';
  name: string | null;
  email: string | null;
  customerUserId: string | null;
  permissions: HouseholdPermissions;
  invitedAt: string | null;
  acceptedAt: string | null;
  isSelf: boolean;
}

interface HouseholdResponse {
  household: {
    customerId: string;
    role: 'primary' | 'member';
    permissions: HouseholdPermissions;
    name: string;
    members: HouseholdMember[];
  } | null;
}

interface InviteResponse {
  id: string;
  invitedEmail: string;
  url: string;
  expiresAt: string;
  reused: boolean;
}

const PERMISSION_LABELS: Array<{ key: keyof HouseholdPermissions; label: string; hint: string }> = [
  { key: 'canBook', label: 'Can book', hint: 'Make new bookings and set up weekly daycare.' },
  { key: 'canPay', label: 'Can pay', hint: 'Pay for bookings and buy packs or memberships.' },
  {
    key: 'canEditPets',
    label: 'Can edit pets',
    hint: 'Add pets, upload vaccination records, change vet details.',
  },
  {
    key: 'receivesNotifications',
    label: 'Gets emails',
    hint: 'Receives booking confirmations, reminders and report cards.',
  },
];

const sectionCard: CSSProperties = {
  background: 'var(--surface, white)',
  border: '1px solid var(--border, oklch(0.92 0.005 80))',
  borderRadius: '12px',
  padding: '1.25rem 1.5rem',
  marginTop: '1.25rem',
};

const buttonStyle: CSSProperties = {
  padding: '0.5rem 0.9rem',
  borderRadius: '8px',
  border: '1px solid var(--border, oklch(0.9 0.005 80))',
  background: 'var(--surface, white)',
  fontSize: '0.85rem',
  cursor: 'pointer',
};

const primaryButton: CSSProperties = {
  ...buttonStyle,
  background: 'var(--brand-primary, #004AAD)',
  color: 'white',
  border: 'none',
  fontWeight: 500,
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof CustomerApiError) {
    const body = err.body as { error?: string; message?: string; issues?: string[] } | null;
    if (body?.message) return body.message;
    if (body?.issues?.length) return body.issues.join(', ');
    if (body?.error === 'already_a_member') return 'That email is already part of this household.';
    if (body?.error === 'primary_only') return 'Only the primary contact can do that.';
    return `${fallback} (${err.status})`;
  }
  return fallback;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function Household() {
  const [data, setData] = useState<HouseholdResponse['household'] | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePerms, setInvitePerms] = useState<HouseholdPermissions>({
    canBook: true,
    canPay: true,
    canEditPets: true,
    receivesNotifications: true,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<InviteResponse | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await customerApi<HouseholdResponse>('/api/customer/household');
      setData(res.household);
      setError(null);
    } catch (err) {
      setData(null);
      setError(errorMessage(err, 'Could not load your household'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const isPrimary = data?.role === 'primary';

  const sendInvite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('invite');
    setError(null);
    setNotice(null);
    try {
      const res = await customerApi<InviteResponse>('/api/customer/household/invites', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, permissions: invitePerms }),
      });
      setLastInvite(res);
      setNotice(
        res.reused
          ? `We've re-sent the invitation to ${res.invitedEmail}.`
          : `Invitation sent to ${res.invitedEmail}. It expires on ${fmtDate(res.expiresAt)}.`,
      );
      setInviteEmail('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not send the invitation'));
    } finally {
      setBusy(null);
    }
  };

  const togglePermission = async (member: HouseholdMember, key: keyof HouseholdPermissions) => {
    setBusy(`${member.id}:${key}`);
    setError(null);
    try {
      await customerApi(`/api/customer/household/members/${member.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ permissions: { [key]: !member.permissions[key] } }),
      });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not update permissions'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (member: HouseholdMember) => {
    const verb = member.isSelf ? 'leave this household' : `remove ${member.email ?? 'this member'}`;
    if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
    setBusy(`${member.id}:remove`);
    setError(null);
    try {
      const res = await customerApi<{ ok: boolean; left: boolean }>(
        `/api/customer/household/members/${member.id}`,
        { method: 'DELETE' },
      );
      if (res.left) {
        window.location.href = '/account';
        return;
      }
      setNotice(member.status === 'pending' ? 'Invitation withdrawn.' : 'Member removed.');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not remove the member'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <BrandedShell>
      <main className="mx-auto max-w-2xl px-4 pt-6 pb-12 sm:px-6 sm:pt-10">
        <p style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
          <Link to="/account" style={{ color: 'var(--muted-foreground)' }}>
            ← Back to account
          </Link>
        </p>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 500, letterSpacing: '-0.01em' }}>Household</h1>
        <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
          Everyone in your household shares the same pets, bookings, credits and history.
        </p>

        {error ? (
          <p
            role="alert"
            style={{
              marginTop: '1rem',
              padding: '0.65rem 0.75rem',
              borderRadius: '8px',
              background: 'oklch(0.97 0.02 28)',
              border: '1px solid oklch(0.88 0.04 28)',
              color: 'var(--destructive, oklch(0.55 0.16 28))',
              fontSize: '0.85rem',
            }}
          >
            {error}
          </p>
        ) : null}
        {notice ? (
          <p
            role="status"
            style={{
              marginTop: '1rem',
              padding: '0.65rem 0.75rem',
              borderRadius: '8px',
              background: 'oklch(0.96 0.03 150)',
              border: '1px solid oklch(0.86 0.05 150)',
              fontSize: '0.85rem',
            }}
          >
            {notice}
          </p>
        ) : null}

        {data === undefined ? (
          <p style={{ color: 'var(--muted-foreground)', marginTop: '1.5rem' }}>Loading…</p>
        ) : data === null ? (
          <section style={sectionCard}>
            <h2 style={{ fontSize: '1rem', fontWeight: 500 }}>No household yet</h2>
            <p
              style={{
                color: 'var(--muted-foreground)',
                fontSize: '0.875rem',
                marginTop: '0.4rem',
              }}
            >
              Your household is created the first time you add a pet or make a booking. Once it
              exists you can invite a partner or family member to share it.
            </p>
          </section>
        ) : (
          <>
            <section style={sectionCard} aria-labelledby="household-members">
              <h2 id="household-members" style={{ fontSize: '1rem', fontWeight: 500 }}>
                Members
              </h2>
              <p
                style={{
                  color: 'var(--muted-foreground)',
                  fontSize: '0.8rem',
                  marginTop: '0.25rem',
                }}
              >
                {isPrimary
                  ? 'You are the primary contact. You can change what each member is allowed to do.'
                  : `${data.name || 'The primary contact'} manages this household. Ask them if you need extra permissions.`}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
                {data.members.map((m) => (
                  <li
                    key={m.id}
                    data-testid={`member-${m.role}-${m.status}`}
                    style={{
                      padding: '0.85rem 0',
                      borderTop: '1px solid var(--border, oklch(0.94 0.005 80))',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>
                          {m.name ?? m.email ?? 'Invited member'}
                          {m.isSelf ? (
                            <span style={{ color: 'var(--muted-foreground)', fontWeight: 400 }}>
                              {' '}
                              (you)
                            </span>
                          ) : null}
                        </div>
                        <div style={{ color: 'var(--muted-foreground)', fontSize: '0.8rem' }}>
                          {m.name && m.email ? `${m.email} · ` : ''}
                          {m.role === 'primary'
                            ? 'Primary contact'
                            : m.status === 'pending'
                              ? `Invited ${fmtDate(m.invitedAt)} — not yet accepted`
                              : 'Co-owner'}
                        </div>
                      </div>
                      {m.role !== 'primary' && (isPrimary || m.isSelf) ? (
                        <button
                          type="button"
                          style={buttonStyle}
                          disabled={busy === `${m.id}:remove`}
                          onClick={() => void remove(m)}
                        >
                          {m.isSelf
                            ? 'Leave household'
                            : m.status === 'pending'
                              ? 'Withdraw invite'
                              : 'Remove'}
                        </button>
                      ) : null}
                    </div>
                    {m.role === 'member' ? (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem 1rem',
                          marginTop: '0.6rem',
                        }}
                      >
                        {PERMISSION_LABELS.map((p) => (
                          <label
                            key={p.key}
                            title={p.hint}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              fontSize: '0.8rem',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={m.permissions[p.key]}
                              disabled={!isPrimary || busy === `${m.id}:${p.key}`}
                              onChange={() => void togglePermission(m, p.key)}
                              aria-label={`${p.label} — ${m.email ?? m.name ?? 'member'}`}
                            />
                            {p.label}
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>

            {isPrimary ? (
              <section style={sectionCard} aria-labelledby="household-invite">
                <h2 id="household-invite" style={{ fontSize: '1rem', fontWeight: 500 }}>
                  Invite a co-owner
                </h2>
                <p
                  style={{
                    color: 'var(--muted-foreground)',
                    fontSize: '0.8rem',
                    marginTop: '0.25rem',
                  }}
                >
                  They'll get an email with a link. They sign in with their own email address — no
                  passwords to share.
                </p>
                <form onSubmit={sendInvite} style={{ marginTop: '0.9rem' }}>
                  <label
                    htmlFor="household-invite-email"
                    style={{
                      display: 'block',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      color: 'var(--muted-foreground)',
                      marginBottom: '0.4rem',
                    }}
                  >
                    Email
                  </label>
                  <input
                    id="household-invite-email"
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border, oklch(0.9 0.005 80))',
                      fontSize: '0.95rem',
                      background: 'var(--background, #fafaf7)',
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem 1rem',
                      marginTop: '0.75rem',
                    }}
                  >
                    {PERMISSION_LABELS.map((p) => (
                      <label
                        key={p.key}
                        title={p.hint}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontSize: '0.8rem',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={invitePerms[p.key]}
                          onChange={(e) =>
                            setInvitePerms((prev) => ({ ...prev, [p.key]: e.target.checked }))
                          }
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                  <button
                    type="submit"
                    style={{ ...primaryButton, marginTop: '1rem' }}
                    disabled={busy === 'invite'}
                  >
                    {busy === 'invite' ? 'Sending…' : 'Send invitation'}
                  </button>
                </form>
                {lastInvite ? (
                  <p
                    style={{
                      color: 'var(--muted-foreground)',
                      fontSize: '0.75rem',
                      marginTop: '0.75rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    Or share this link directly: {lastInvite.url}
                  </p>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </main>
    </BrandedShell>
  );
}
